import { getOrFetchDocData } from "../context/doc-context";
import { selectPageStatusLine } from "../context/page-context";
import { docToPageAndAnswerContext } from "../queries/doc-query";

import { ToolType } from "./types";

import { t } from "i18n/i18n";
import { store } from "store/index";
import { selectActivityById, selectPageById, selectTopicById } from "store/selectors";

/**
 * A tool that allows the AI to request the worksheet data of a specific page,
 * returning it as context and re-sending the chat query.
 */
export const readPage = async (params: { pageId: string }) => {
  const state = store.getState();
  const page = selectPageById(state, params.pageId);
  if (!page) return "The page does not exist, or could not be loaded.";

  const docData = await getOrFetchDocData(page.worksheet_id, null);
  const { context } = docToPageAndAnswerContext(docData, page.id, []);

  const statusLine = selectPageStatusLine(state, page);
  // TODO: Make files available to the AI by sending them to the backend
  return {
    page: statusLine,
    content: context,
    files: "Referenced files are not attached and cannot be viewed.",
  };
};

const readPageProgressMessage = (params: { pageId: string }) => {
  const state = store.getState();
  const page = selectPageById(state, params.pageId);
  // Might not be in the store, which is unlikely, but fine, we'll leave
  // error handling to the actual tool call.
  if (!page) return "Reading page content";

  // TODO: I feel like this logic exists somewhere else, but I couldn't find it,
  // might be worth de-duplicating at some point.
  const pageCategoryText = t(`glossary.${page.category}`);

  const maybeActivity = selectActivityById(state, page.activity_id);
  const maybeTopic = selectTopicById(state, maybeActivity?.topic_id);

  return `Reading content for ${pageCategoryText} "${page.title}"${maybeTopic ? ` of Topic "${maybeTopic.title}"` : ""}`;
};

export const readPageTool: ToolType<{ pageId: string }> = {
  name: "read_page",
  description:
    "Read any Page by ID, including metadata, instructions, and any student responses (if enabled by user).",
  progressMessage: readPageProgressMessage,
  parameters: {
    type: "object",
    properties: {
      pageId: {
        type: "string",
        description: "The ID of the page whose content to read",
      },
    },
    required: ["pageId"],
    additionalProperties: false,
  },
  callback: readPage,
};
